import React from "react";
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import { LegendItem, MONO_FONT, headCellSx, numCellSx, textCellSx } from "../../components/metrics/metricsUi";
import {
  coverageColor,
  fmtDay,
  fmtLead,
  fmtMonth,
  fmtPct,
  fmtUnits,
} from "../../utils/purchaseOrderAnalysisPageDefaults";
import { SectionCard, Stat, tooltipContentStyle } from "./parts";

const PointTooltip = ({ active, payload, ui }) => {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0].payload;
  return (
    <Box sx={{ ...tooltipContentStyle(ui), p: 1.25 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 650, fontFamily: MONO_FONT }}>{point.po_no}</Typography>
      <Typography sx={{ fontSize: 12 }}>
        Raised {fmtDay(point.po_date)} · received {fmtDay(point.received_date)}
      </Typography>
      <Typography sx={{ fontSize: 12 }}>
        Promised {fmtLead(point.planned_days)} · took {fmtLead(point.lead_days)}
      </Typography>
      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
        {point.days_late > 0 ? `${point.days_late} days late` : point.days_late < 0 ? `${-point.days_late} days early` : "On the day"}
      </Typography>
    </Box>
  );
};

const ReliabilitySection = ({ ui, overview, onOpenPo }) => {
  const { colors } = ui;
  const { summary, monthly, suppliers, scatter } = overview.reliability;
  const onTimeColor = coverageColor("covered", ui.darkMode);
  const lateColor = coverageColor("late", ui.darkMode);
  const points = scatter.filter((point) => point.planned_days != null && point.days_late != null);
  const onTime = points.filter((point) => point.days_late <= 0);
  const late = points.filter((point) => point.days_late > 0);
  const maxDays = Math.max(10, ...points.map((point) => Math.max(point.planned_days, point.lead_days)));
  const axisMax = Math.ceil(maxDays / 10) * 10;
  const lookback = overview.lookback.months;

  if (!summary.received_pos) {
    return (
      <SectionCard
        ui={ui}
        title="Supplier reliability"
        subtitle={`No POs raised in the last ${lookback} months have been received at this warehouse yet.`}
        sx={{ height: "100%" }}
      />
    );
  }

  const headline =
    summary.on_time_pct == null
      ? `${fmtUnits(summary.received_pos)} POs received; median lead time ${fmtLead(summary.median_lead_days)}.`
      : `${fmtPct(summary.on_time_pct)} of POs arrived on time; the late ones were ${fmtLead(summary.avg_days_late)} late on average.`;

  return (
    <SectionCard ui={ui} title="Supplier reliability" subtitle={headline} sx={{ height: "100%" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 1.5, mb: 2 }}>
        <Stat ui={ui} label="On time" value={fmtPct(summary.on_time_pct)} sub={`${fmtUnits(summary.on_time_pos)} of ${fmtUnits(summary.timed_pos)} POs`} />
        <Stat ui={ui} label="Lead time" value={fmtLead(summary.median_lead_days)} sub={`median · p90 ${fmtLead(summary.p90_lead_days)}`} />
        <Stat ui={ui} label="Promised" value={fmtLead(summary.median_planned_days)} sub="median, PO → ETA" />
        <Stat ui={ui} label="Fill rate" value={fmtPct(summary.fill_rate_pct)} sub="units received ÷ ordered" />
      </Box>

      {points.length ? (
        <>
          <Typography sx={{ fontSize: 12, fontWeight: 650, color: colors.textPrimary, mb: 0.5 }}>
            Promised vs actual lead time, per PO
          </Typography>
          <Box sx={{ height: 210 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 8, right: 12, left: -8, bottom: 4 }}>
                <CartesianGrid stroke={colors.border} />
                <XAxis
                  type="number"
                  dataKey="planned_days"
                  name="Promised"
                  domain={[0, axisMax]}
                  tick={{ fontSize: 10.5, fill: colors.textSecondary }}
                  tickLine={false}
                  axisLine={{ stroke: colors.border }}
                  label={{ value: "Promised days (PO → expected)", position: "insideBottom", offset: -2, fontSize: 10.5, fill: colors.textSecondary }}
                  height={34}
                />
                <YAxis
                  type="number"
                  dataKey="lead_days"
                  name="Actual"
                  domain={[0, axisMax]}
                  tick={{ fontSize: 10.5, fill: colors.textSecondary }}
                  tickLine={false}
                  axisLine={false}
                  width={44}
                  label={{ value: "Actual days", angle: -90, position: "insideLeft", offset: 18, fontSize: 10.5, fill: colors.textSecondary, style: { textAnchor: "middle" } }}
                />
                <ZAxis range={[46, 46]} />
                {/* On the line = arrived exactly as promised; above it = late. */}
                <ReferenceLine
                  segment={[{ x: 0, y: 0 }, { x: axisMax, y: axisMax }]}
                  stroke={colors.textTertiary}
                  strokeDasharray="4 4"
                  label={{ value: "as promised", position: "insideTopRight", fontSize: 10, fill: colors.textTertiary }}
                />
                <RechartsTooltip content={<PointTooltip ui={ui} />} cursor={false} />
                <Scatter name="On time" data={onTime} fill={onTimeColor} stroke={colors.bgPaper} strokeWidth={2} isAnimationActive={false} onClick={(point) => onOpenPo(point.po_id || point.payload?.po_id)} cursor="pointer" />
                <Scatter name="Late" data={late} fill={lateColor} stroke={colors.bgPaper} strokeWidth={2} isAnimationActive={false} onClick={(point) => onOpenPo(point.po_id || point.payload?.po_id)} cursor="pointer" />
              </ScatterChart>
            </ResponsiveContainer>
          </Box>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 0.5, mb: 2 }}>
            <LegendItem color={onTimeColor} label={`On time / early (${fmtUnits(onTime.length)})`} />
            <LegendItem color={lateColor} label={`Late (${fmtUnits(late.length)})`} />
          </Box>
        </>
      ) : null}

      {monthly.length ? (
        <>
          <Typography sx={{ fontSize: 12, fontWeight: 650, color: colors.textPrimary, mb: 0.5 }}>
            POs received per month
          </Typography>
          <Box sx={{ height: 150 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthly} margin={{ top: 4, right: 4, left: -8, bottom: 0 }} barCategoryGap="28%">
                <CartesianGrid vertical={false} stroke={colors.border} />
                <XAxis dataKey="month" tickFormatter={fmtMonth} tick={{ fontSize: 10.5, fill: colors.textSecondary }} tickLine={false} axisLine={{ stroke: colors.border }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10.5, fill: colors.textSecondary }} tickLine={false} axisLine={false} width={40} />
                <RechartsTooltip
                  cursor={{ fill: ui.surfaceAlt }}
                  labelFormatter={fmtMonth}
                  formatter={(value, name) => [fmtUnits(value), name]}
                  contentStyle={tooltipContentStyle(ui)}
                />
                <Bar dataKey="on_time_pos" name="On time" stackId="m" fill={onTimeColor} stroke={colors.bgPaper} strokeWidth={2} maxBarSize={24} isAnimationActive={false} />
                <Bar dataKey="late_pos" name="Late" stackId="m" fill={lateColor} stroke={colors.bgPaper} strokeWidth={2} maxBarSize={24} radius={[4, 4, 0, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </>
      ) : null}

      {suppliers.length > 1 ? (
        <Table size="small" sx={{ mt: 2 }}>
          <TableHead>
            <TableRow>
              {["Supplier", "POs", "On time", "Avg late", "Lead (median)", "Fill"].map((label, index) => (
                <TableCell key={label} sx={headCellSx(ui, { textAlign: index ? "right" : "left" })}>{label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {suppliers.map((row) => (
              <TableRow key={row.supplier_id || "none"}>
                <TableCell sx={textCellSx(ui)}>{row.supplier_name || "No supplier"}</TableCell>
                <TableCell sx={numCellSx(ui, { fontFamily: MONO_FONT })}>{fmtUnits(row.received_pos)}</TableCell>
                <TableCell sx={numCellSx(ui, { fontFamily: MONO_FONT })}>{fmtPct(row.on_time_pct)}</TableCell>
                <TableCell sx={numCellSx(ui, { fontFamily: MONO_FONT })}>{fmtLead(row.avg_days_late)}</TableCell>
                <TableCell sx={numCellSx(ui, { fontFamily: MONO_FONT })}>{fmtLead(row.median_lead_days)}</TableCell>
                <TableCell sx={numCellSx(ui, { fontFamily: MONO_FONT })}>{fmtPct(row.fill_rate_pct)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : suppliers.length === 1 && suppliers[0].supplier_name ? (
        <Typography sx={{ fontSize: 11.5, color: colors.textTertiary, mt: 1 }}>
          All received POs here are from {suppliers[0].supplier_name}.
        </Typography>
      ) : null}
      <Typography sx={{ fontSize: 11.5, color: colors.textTertiary, mt: 1, lineHeight: 1.55 }}>
        POs raised in the last {lookback} months and received here. Lead time = PO date → first receipt.
        {overview.data_quality.reliability_excluded.same_day_receipt
          ? ` ${fmtUnits(overview.data_quality.reliability_excluded.same_day_receipt)} POs raised the day they were received are left out.`
          : ""}
      </Typography>
    </SectionCard>
  );
};

export default ReliabilitySection;
